import { type ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

/**
 * The modal shell every admin dialog was writing by hand.
 *
 * NOT A REFACTOR. Five dialogs in this app rendered their own
 * `fixed inset-0 ... role="dialog"` overlay, and exactly one of them -- the
 * bulk confirmation, and only after a review round asked for it -- moved focus
 * in, kept Tab inside, closed on Escape and put focus back. The other four
 * included the brand MERGE and the template REMOVAL, both destructive and both
 * reachable only by keyboard operators who could Tab straight out of them into
 * the page behind, change a filter or press a button they could not see, and
 * come back to a dialog describing something that had moved.
 *
 * So the behaviour lives here once, and a new dialog gets it by construction
 * rather than by whoever writes it remembering.
 *
 * WHAT IT DELIBERATELY DOES NOT DO: close on a backdrop click. These are
 * confirmations for things like retiring a brand and stranding its products;
 * dismissing one with a stray click beside the panel is not a decision anybody
 * made. Escape is the deliberate way out, and it is the safe direction.
 */
export function Dialog({
	open,
	titleId,
	onClose,
	children,
	className = "grid w-full max-w-lg gap-3 rounded-3xl border bg-white p-5 shadow-lg outline-none",
}: {
	open: boolean;
	/** The id of the heading inside, for `aria-labelledby`. */
	titleId: string;
	onClose: () => void;
	children: ReactNode;
	className?: string;
}) {
	const panelRef = useRef<HTMLDivElement>(null);
	const returnTo = useRef<HTMLElement | null>(null);

	/*
	 * `onClose` through a ref so this effect depends on `open` ALONE.
	 *
	 * Callers pass inline arrows, which are a fresh identity every render. With
	 * one in the dependency list, every re-render behind the dialog tears the
	 * effect down and sets it up again -- and the teardown restores focus to the
	 * trigger before the setup pulls it back inside, so focus an operator had
	 * moved to Cancel is silently dragged back to the confirm button.
	 */
	const closeRef = useRef(onClose);
	closeRef.current = onClose;

	useEffect(() => {
		if (!open) return;
		returnTo.current = document.activeElement as HTMLElement | null;

		const panel = panelRef.current;
		/*
		 * Deliberately WITHOUT an `aria-hidden` / `tabindex="-1"` filter, having
		 * had one and taken it out again.
		 *
		 * It was added for Radix's hidden native <select>, on the theory that it
		 * could become the "first" control the trap focuses. Rendering the real
		 * merge-dialog shape inside the shell and dumping both selectors gave
		 * identical three-element lists: nothing carrying `tabindex`, nothing
		 * `aria-hidden`, and the same element focused either way. Both halves of
		 * the change also survived mutation -- deleting the filter, and deleting
		 * the `[tabindex]` clause it was there to filter, each left the whole
		 * admin suite green.
		 *
		 * A guard that changes no behaviour and that no test can distinguish is
		 * a comment claiming to do something. If a portalled or hidden control
		 * ever does land inside a panel, this is the place, and the test that
		 * proves it should arrive with it.
		 */
		const focusable = () => [
			...(panel?.querySelectorAll<HTMLElement>(
				// `input[type=hidden]` is excluded because the browser will never
				// focus one, so including it would let the trap "focus" a control
				// that does not take focus -- and on a form dialog carrying a
				// hidden field first, that means focus goes nowhere on open.
				// Unlike the `[tabindex]` clause removed above, this one is
				// testable, and the case below does test it.
				"button:not([disabled]), [href], input:not([type='hidden']):not([disabled]), select:not([disabled]), textarea:not([disabled])",
			) ?? []),
		];

		// The first control, or the panel itself when there is none -- which is
		// how a dialog that only reports something still moves focus in and gets
		// itself announced.
		(focusable()[0] ?? panel)?.focus();

		const onKey = (event: KeyboardEvent) => {
			/*
			 * SOMEBODY ELSE ALREADY ANSWERED THIS KEY.
			 *
			 * Radix's dismissable layer calls `preventDefault()` on Escape and
			 * deliberately does NOT stop propagation, so an Escape that closed an
			 * open `Select` still reaches this listener. Without this check,
			 * dismissing a dropdown inside a dialog also dismisses the dialog
			 * underneath it, discarding whatever the operator had entered in one
			 * keypress.
			 */
			if (event.defaultPrevented) return;

			if (event.key === "Escape") {
				closeRef.current();
				return;
			}
			if (event.key !== "Tab" || !panel) return;
			const items = focusable();
			if (items.length === 0) {
				event.preventDefault();
				panel.focus();
				return;
			}
			const first = items[0];
			const last = items[items.length - 1];
			const active = document.activeElement;

			/*
			 * FOCUS OUTSIDE THE PANEL COMES BACK IN, and this is the branch the
			 * first version was missing.
			 *
			 * Clicking the dim area beside the panel moves focus to <body> -- and
			 * the shell deliberately does not treat that as a dismiss, so it is a
			 * supported state, not a transient one. With the trap keyed only on
			 * `first`/`last`/`panel`, none matched from <body>, nothing called
			 * `preventDefault`, and the next Tab went to the first tabbable element
			 * in the DOCUMENT -- the page behind. On the brand merge that is the
			 * brand list and its filters, which is exactly the escape this shell's
			 * docblock claims to prevent.
			 */
			if (!panel.contains(active)) {
				event.preventDefault();
				first.focus();
				return;
			}

			if (event.shiftKey && (active === first || active === panel)) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && (active === last || active === panel)) {
				event.preventDefault();
				first.focus();
			}
		};

		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("keydown", onKey);
			returnTo.current?.focus();
		};
	}, [open]);

	if (!open) return null;

	/*
	 * PORTALLED TO <body>. The admin shell's panels use `backdrop-blur`, and a
	 * `backdrop-filter` makes an element the containing block for its `fixed`
	 * descendants -- so rendered in place, this overlay covered only the main
	 * panel and centred the dialog on the panel's full height. On a long list
	 * that put the dialog a screen or more below the one the operator was
	 * looking at (#514's GST rate list made it visible at 390 px).
	 */
	return createPortal(
		<div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
			<div
				ref={panelRef}
				tabIndex={-1}
				role="dialog"
				aria-modal="true"
				aria-labelledby={titleId}
				className={className}
			>
				{children}
			</div>
		</div>,
		document.body,
	);
}
